import './MeetingSchedulingMonthView.scss';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import moment, { Moment } from 'moment';

interface AllowedDatesRange {
    startDate: Date;
    endDate: Date;
}

interface MeetingSchedulingMonthViewProps {
    engagementId: number;
    startDate: Date;
    endDate: Date;
    highlightedDates?: Date[];
    selectedDate?: Date;
    onDateSelect?: (date: Date) => void;
    onMonthSelect?: (month: Date) => void;
    allowedDates?: AllowedDatesRange | null;
    viewMode?:
        | 'buyerTopLevel'
        | 'sellerTopLevel'
        | 'engagement'
        | 'opportunity';
}

export const MeetingSchedulingMonthView = (
    props: MeetingSchedulingMonthViewProps
) => {
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [hasAutoNavigated, setHasAutoNavigated] = useState(false);

    useEffect(() => {
        if (hasAutoNavigated) {
            return;
        }

        const isEngagementOrOpportunity = props.viewMode === 'engagement' || props.viewMode === 'opportunity';

        if (isEngagementOrOpportunity && props.allowedDates) {
            const startDate = moment(props.allowedDates.startDate);
            const currentMonthStart = currentMonth.clone().startOf('month');
            const currentMonthEnd = currentMonth.clone().endOf('month');
            
            const allowedDateIsOutsideCurrentMonth = !startDate.isBetween(currentMonthStart, currentMonthEnd, 'day', '[]');

            if (allowedDateIsOutsideCurrentMonth) {
                setCurrentMonth(startDate);
                
                if (props.onDateSelect) {
                    props.onDateSelect(startDate.toDate());
                }

                setHasAutoNavigated(true);
            }
        }
    }, [props.allowedDates, props.viewMode, hasAutoNavigated, props.onDateSelect, currentMonth]);

    const weekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

    const calendarDays = useMemo(() => {
        const startOfMonth = currentMonth.clone().startOf('month');
        const endOfMonth = currentMonth.clone().endOf('month');

        // Monday of the first week
        const startDate = startOfMonth.clone().startOf('isoWeek');
        // Sunday of the last week
        const endDate = endOfMonth.clone().endOf('isoWeek');

        const days: Moment[] = [];
        const day = startDate.clone();

        while (day.isSameOrBefore(endDate)) {
            days.push(day.clone());
            day.add(1, 'day');
        }

        return days;
    }, [currentMonth]);

    const isDateHighlighted = (date: Moment) => {
        if (!props.highlightedDates || props.highlightedDates.length === 0)
            return false;
        const current = date.clone().startOf('day');

        return props.highlightedDates.some((highlightedDate) => {
            const highlighted = moment(highlightedDate).startOf('day');
            return current.isSame(highlighted);
        });
    };

    const isDateSelected = (date: Moment) => {
        if (!props.selectedDate) return false;
        const selected = moment(props.selectedDate).startOf('day');
        const current = date.clone().startOf('day');

        return current.isSame(selected);
    };

    const isDateAllowed = (date: Moment) => {
        if (!props.allowedDates) return true;

        const current = date.clone().startOf('day');
        const rangeStart = moment(props.allowedDates.startDate).startOf('day');
        const rangeEnd = moment(props.allowedDates.endDate).endOf('day');

        return (
            current.isSameOrAfter(rangeStart) &&
            current.isSameOrBefore(rangeEnd)
        );
    };

    const canGoPrev = !props.allowedDates
        || currentMonth.clone().subtract(1, 'month').endOf('month')
            .isSameOrAfter(moment(props.allowedDates.startDate).startOf('day'));

    const canGoNext = !props.allowedDates
        || currentMonth.clone().add(1, 'month').startOf('month')
            .isSameOrBefore(moment(props.allowedDates.endDate).endOf('day'));

    const handlePreviousMonth = () => {
        const newMonth = currentMonth.clone().subtract(1, 'month');
        setCurrentMonth(newMonth);
        if (props.onMonthSelect) {
            props.onMonthSelect(newMonth.toDate());
        }
    };

    const handleNextMonth = () => {
        const newMonth = currentMonth.clone().add(1, 'month');
        setCurrentMonth(newMonth);
        if (props.onMonthSelect) {
            props.onMonthSelect(newMonth.toDate());
        }
    };

    const handleDateClick = (date: Moment) => {
        if (!isDateAllowed(date)) return;

        if (props.onDateSelect) {
            props.onDateSelect(date.toDate());
        }
    };

    return (
        <div className="meeting-scheduling-month-view">
            <div className="calendar-header">
                <button
                    className="nav-button"
                    onClick={handlePreviousMonth}
                    disabled={!canGoPrev}
                    aria-label="Previous month">
                    &lt;
                </button>
                <h2 className="month-year">
                    {currentMonth.format('MMMM YYYY')}
                </h2>
                <button
                    className="nav-button"
                    onClick={handleNextMonth}
                    disabled={!canGoNext}
                    aria-label="Next month">
                    &gt;
                </button>
            </div>

            <div className="calendar-grid">
                <div className="weekday-header">
                    {weekDays.map((day) => (
                        <div key={day} className="weekday">
                            {day}
                        </div>
                    ))}
                </div>

                <div className="days-grid">
                    {calendarDays.map((day, index) => {
                        const isCurrentMonth =
                            day.month() === currentMonth.month();
                        const isHighlighted = isDateHighlighted(day);
                        const isSelected = isDateSelected(day);
                        const isAllowed = isDateAllowed(day);

                        return (
                            <div
                                key={index}
                                className={`day-cell ${
                                    !isCurrentMonth ? 'other-month' : ''
                                } ${isHighlighted ? 'highlighted' : ''} ${isSelected ? 'selected' : ''} ${!isAllowed ? 'disabled' : ''}`}
                                onClick={() => handleDateClick(day)}>
                                <span className="day-number">{day.date()}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
